import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import Countdown from 'react-countdown';

import { request } from 'utilities/graph';
import {
  getIssuesByClientByCreatedAt,
  getElder,
} from 'graphql/queries';
import {
  Colors,
  issueStatus,
  AUTO_UPDATE_INTERVAL_IN_MILLISECONDS,
} from '@silvergatedelivery/constants';
import { sortBy } from 'utilities/sorting';
import { formatDatetimeFromNow } from 'utilities/format';
// TODO: move to common components
import Issue from 'views/Admin/IssueCenter/components/Issue';
import cache from 'utilities/cache';
import { asyncListAll } from 'utilities/graph';
import { getEldersByClientByStatus } from './Forms/queries';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
  },
  listContainer: {
    position: 'relative',
    overflow: 'auto',
    overflowX: 'hidden',
    maxHeight: 'calc(100vh - 116px)',
    paddingRight: 8,
    paddingLeft: 8,
  },
  list: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    // position: 'relative',
    // overflow: 'auto',
    // maxHeight: 'calc(100vh - 200px)',
  },
  chip: {
    border: '1px solid #cecece',
    borderRadius: 3,
    padding: 1,
    display: 'inline-block',
    marginRight: 6,
    marginBottom: 6,
  },
  issueContainer: {
    position: 'relative',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 116px)',
  },
  selected: {
    borderLeft: `6px solid ${Colors.primary}`,
    paddingLeft: 10,
  },
  selectChips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  selectChip: {
    margin: 2,
  },
  filters: {
    padding: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(1),
  },
}));

export default function IssueCenter() {
  const classes = useStyles();
  const [issues, setIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState();
  const [query, setQuery] = useState({
    status: ['pending', 'processing'],
  });
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadAdmins, setHasLoadAdmins] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState();
  const [elders, setElders] = useState([]);
  const clientId = cache.get('app:facilityId');
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    (async () => {
      const elders = await asyncListAll(getEldersByClientByStatus, {
        clientId,
        status: { eq: '使用中' },
      });
      setElders(elders);
    })();
  }, []);

  useEffect(() => {
    if (!issues.length) return;
    if (!elders.length) return;

    const missingElderIds = issues
      .map((issue) => issue.elderId)
      .filter((elderId) => elderId && !elders.find((elder) => elder.id === elderId));

    if (missingElderIds.length > 0) {
      (async () => {
        const newElders = await Promise.all(
          missingElderIds.map(async (elderId) => {
            const { data: { getElder: elder } } = await request(getElder, { id: elderId });
            return elder;
          }),
        );
        setElders([...elders, ...newElders]);
      })();
    }
  }, [elders, issues]);

  const renderChipValue = (selected) => (
    <div className={classes.selectChips}>
      {selected.map((value) => {
        const matchedStatus = issueStatus.find((item) => item.value === value);
        const label = matchedStatus ? matchedStatus.label : value;
        const backgroundColor = matchedStatus ? matchedStatus.color : undefined;
        const color = backgroundColor ? '#fff' : undefined;

        return (<Chip key={value}
          label={label}
          className={classes.selectChip}
          style={{ color, backgroundColor }}
        />);
      })}
    </div>
  );

  const updateQuery = (key, value) => {
    query[key] = value;
    setQuery({ ...query });
  };

  useEffect(() => {
    if (hasLoadAdmins) return;
    (async () => {
      setHasLoadAdmins(true);
    })();
  }, [hasLoadAdmins]);

  const fetchIssues = async (isRefresh = false) => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      const { status } = query;
      let newData = [];
      let newNextToken;
      let newIssuesFetchedCount = 0;

      if (status.length > 0) {
        const currentToken = isRefresh ? null : nextToken;
        do {
          const params = {
            clientId,
            filter: {
              or: status.map((item) => ({ status: { eq: item } })),
            },
            limit: ITEMS_PER_PAGE,
            sortDirection: 'DESC',
            nextToken: newNextToken || currentToken,
          };
          const { data: { getIssuesByClientByCreatedAt: { items, nextToken: nextTokenFromServer } } } = await request(getIssuesByClientByCreatedAt, params);
          newNextToken = nextTokenFromServer;
          if (items) {
            newData = [...newData, ...items];
            newIssuesFetchedCount += items.length;
          }
        } while (newNextToken && newIssuesFetchedCount < ITEMS_PER_PAGE);
        setNextToken(newNextToken);
        setHasMore(!!newNextToken);
      } else {
        setNextToken();
        setHasMore(false);
      }

      newData = newData.sort(sortBy('createdAt', true));

      if (isRefresh) {
        setIssues(newData);
        setSelectedIssue(newData[0]);
      } else {
        setIssues((prev) => [...prev, ...newData]);
      }
    } catch (e) {
      global.logger.debug(e);
      setNextToken();
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!query) return;
    fetchIssues(true);
  }, [query, lastUpdatedAt, clientId]);

  const getElderName = (elderId) => {
    const elder = elders.find((elder) => elder.id === elderId);
    return elder ? elder.name : '';
  };

  return (
    <Grid container className={classes.root}>
      <Grid item sm={12} md={2} className={classes.listContainer}>
        <Grid container className={classes.filters} direction="column">
          <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: -4, marginBottom: 4 }}>
            距離下次自動更新：
            {!isLoading &&
            <Countdown
              date={lastUpdatedAt + AUTO_UPDATE_INTERVAL_IN_MILLISECONDS}
              daysInHours={true}
              onComplete={() => setLastUpdatedAt(Date.now())}
            />}
            <IconButton size="small" disabled={isLoading} onClick={() => setLastUpdatedAt(Date.now())}>
              <RefreshIcon />
            </IconButton>
          </Typography>
          <FormControl className={classes.formControl} fullWidth>
            <InputLabel>狀態</InputLabel>
            <Select
              multiple
              value={query.status}
              onChange={(event) => updateQuery('status', event.target.value)}
              input={<Input id="select-multiple-issueStatus" />}
              renderValue={renderChipValue}
              disabled={isLoading}
            >
              {issueStatus.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={query.status.indexOf(item.value) > -1} />
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <List
          className={classes.list}
          id="issueList"
          style={{ height: 'calc(100vh - 256px)', overflow: 'auto' }}
        >
          <InfiniteScroll
            dataLength={issues.length}
            next={() => {
              return fetchIssues(false);
            }}
            style={{ overflow: 'visible' }}
            hasMore={hasMore}
            loader={<Typography align="center">載入中...</Typography>}
            endMessage={<Typography align="center">{isLoading ? '載入中...' : '沒有更多資料了'}</Typography>}
            scrollableTarget="issueList"
            scrollThreshold={0.8}
          >
            {issues.map((issue, index) => (
              <ListItem
                key={index}
                button
                divider
                onClick={() => {
                  global.logger.debug(issue);
                  setSelectedIssue(issue);
                }}
                disabled={isLoading}
                className={selectedIssue && selectedIssue.id === issue.id ? classes.selected : ''}
              >
                <Grid container direction="column">
                  {issue.summary !== issue.type && (
                    <Typography variant="body1" noWrap={true}>
                      {issue.summary}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    {getElderName(issue.elderId)}：{issue.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    <IssueChips issue={issue} />
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    更新於 {formatDatetimeFromNow(issue.updatedAt)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    通報於 {formatDatetimeFromNow(issue.createdAt)}
                  </Typography>
                </Grid>
              </ListItem>
            ))}
          </InfiniteScroll>
        </List>
      </Grid>
      <Grid item md={10} className={classes.issueContainer}>
        <Issue
          issue={selectedIssue}
          onUpdate={() => setLastUpdatedAt(Date.now())}
        />
      </Grid>
    </Grid>
  );
}

function IssueChips({ issue }) {
  const classes = useStyles();
  const { label: statusLabel, color: statusColor } = issueStatus.find(({ value }) => value === issue.status);
  return (
    <React.Fragment>
      <span className={classes.chip} style={{ color: statusColor, borderColor: statusColor }}>{statusLabel}</span>
      <span className={classes.chip}>{issue.type}</span>
      {issue.assignee && <span className={classes.chip}>{issue.assignee}</span>}
    </React.Fragment>);
}

IssueChips.propTypes = {
  issue: PropTypes.object.isRequired,
};
